// =============================
// Permissions: single source of truth
// =============================
export const PermissionNames = [
    "Access module Instruments",
    "Access module Sample Setup",
    "Access module Runs",
    "Access module Data Management",
    "Access module SMRT Analysis",
] as const;

export type PermissionName = (typeof PermissionNames)[number];

export const [
    Access_module_Instruments,
    Access_module_Sample_setup,
    Access_module_Run_design,
    Access_module_Data_management,
    Access_module_SMRT_analysis,
] = PermissionNames;

// =============================
// Types
// =============================
export type SLPermission = {
    name: PermissionName;
    isAllowed: boolean;
};

export type SLUserRole = {
    type: string;           // runtime string for flexibility
    label: string;
    Permissions: SLPermission[];
};

// =============================
// Canonical permission set
// =============================
export const DefaultPermissions: SLPermission[] = PermissionNames.map(name => ({
    name,
    isAllowed: false,
}));

// Utility to build a permission array with selected names set to allowed
function setAllowed(allowedNames: readonly PermissionName[]): SLPermission[] {
    const allow = new Set<PermissionName>(allowedNames);
    return DefaultPermissions.map(p => ({
        name: p.name,
        isAllowed: allow.has(p.name),
    }));
}

// =============================
// Roles: single source of truth (+ derived RoleType)
// =============================
const RoleSeed = [
    {
        type: "Internal/PbAdmin",
        label: "Administrator",
        allow: [
            Access_module_Instruments,
            Access_module_Sample_setup,
            Access_module_Run_design,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "Internal/PbLabTech",
        label: "Lab Technician",
        allow: [
            Access_module_Instruments,
            Access_module_Sample_setup,
            Access_module_Run_design,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "Internal/PbBioinformatician",
        label: "Bio Informatician",
        allow: [
            Access_module_Instruments,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "Internal/PbInstrument",
        label: "Instrument",
        allow: [Access_module_Instruments],
    },
    {
        type: "PbAdmin",
        label: "Administrator",
        allow: [
            Access_module_Instruments,
            Access_module_Sample_setup,
            Access_module_Run_design,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "PbLabTech",
        label: "Lab Technician",
        allow: [
            Access_module_Instruments,
            Access_module_Sample_setup,
            Access_module_Run_design,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "PbBioinformatician",
        label: "Bio Informatician",
        allow: [
            Access_module_Instruments,
            Access_module_Data_management,
            Access_module_SMRT_analysis,
        ],
    },
    {
        type: "PbInstrument",
        label: "Instrument",
        allow: [Access_module_Instruments],
    },
] as const;

export type RoleType = (typeof RoleSeed)[number]["type"];

// Build mutable roles from the readonly seed
export const DefaultUserRoles: SLUserRole[] = RoleSeed.map(seed => ({
    type: seed.type,
    label: seed.label,
    Permissions: setAllowed(seed.allow),
}));

// =============================
// Finders
// =============================

// Search ONLY by `type` (case-insensitive), accepts any string
export function FindRoleByName(
    roleType: string,
    roles: SLUserRole[] = DefaultUserRoles
): SLUserRole | undefined {
    const normalized = roleType.trim().toLowerCase();
    return roles.find(r => r.type.toLowerCase() === normalized);
}

// Strictly typed variant: only accepts known RoleType values
export function FindRoleByNameTyped(
    roleType: RoleType,
    roles: SLUserRole[] = DefaultUserRoles
): SLUserRole | undefined {
    return roles.find(r => r.type === roleType);
}

export function GetRolesByNames(
    roleNames: string[],
    roles: SLUserRole[] = DefaultUserRoles
): SLUserRole[] {
    if (!Array.isArray(roleNames) || roleNames.length === 0) {
        return [];
    }

    const normalized = roleNames.map(name => name.trim().toLowerCase());

    return roles.filter(r => normalized.includes(r.type.toLowerCase()));
}

// =============================
// Permission checks & updates
// =============================

// ✅ Modified to take an array of roles
export function IsAllowedToUse(
    roles: SLUserRole[] | undefined,
    permission: PermissionName
): boolean {
    if (!roles || roles.length === 0) return false;

    // Return true if ANY of the roles grants the permission
    return roles.some(role => {
        const perm = role.Permissions.find(p => p.name === permission);
        return !!perm?.isAllowed;
    });
}

export function SetRolePermission(
    role: SLUserRole,
    permissionName: PermissionName,
    isAllowed: boolean,
    opts?: {
        createIfMissing?: boolean;          // default: true
        validateAgainstDefaults?: boolean;  // default: true
        preventDuplicates?: boolean;        // default: true
    }
): void {
    const {
        createIfMissing = true,
        validateAgainstDefaults = true,
        preventDuplicates = true,
    } = opts ?? {};

    if (validateAgainstDefaults) {
        const existsInDefaults = DefaultPermissions.some(p => p.name === permissionName);
        if (!existsInDefaults) {
            throw new Error(
                `Permission "${permissionName}" is not in DefaultPermissions. (role=${role?.type ?? "unknown"})`
            );
        }
    }

    const idx = role.Permissions.findIndex(p => p.name === permissionName);

    if (idx >= 0) {
        role.Permissions[idx].isAllowed = isAllowed;
        return;
    }

    if (!createIfMissing) {
        throw new Error(
            `Permission "${permissionName}" not found on role "${role.label}" (type=${role.type}).`
        );
    }

    if (preventDuplicates && role.Permissions.some(p => p.name === permissionName)) {
        return; // safeguard against accidental dupes
    }

    role.Permissions.push({ name: permissionName, isAllowed });
}

// Immutable/pure updater
export function WithRolePermission(
    role: SLUserRole,
    permissionName: PermissionName,
    isAllowed: boolean,
    opts?: Parameters<typeof SetRolePermission>[3]
): SLUserRole {
    const clone: SLUserRole = {
        ...role,
        Permissions: role.Permissions.map(p => ({ ...p })),
    };
    SetRolePermission(clone, permissionName, isAllowed, opts);
    return clone;
}
